import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const buildLongVideoSkill: Skill = {
    name: SKILL_NAMES.buildLongVideo,
    description: `Building or filling in a long video: narration longer than about 3 minutes, or more than about 20 scenes. Load BEFORE building it, alongside the video's own skills: it splits the work into sections and fills them in parallel with ${TOOL_NAMES.runSubagent}. Not for a subagent.`,
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Building a Long Video

A long video built scene by scene in one conversation runs slowly and buries your context in
detail. Build it the way an editor would with a team: plan the sections yourself, then hand
each section to a subagent (\`${TOOL_NAMES.runSubagent}\`) to fill in, several at a time.

If you are a subagent, ignore this skill: you can't start subagents, so build your part yourself.

## 1. Plan the sections
Split the content into sections of about a minute of narration (roughly 150 words), or 5-10
scenes for a video without narration. Put the boundaries at natural breaks: a new topic, step,
or speaker. Write the plan down for yourself: each section's purpose and where it starts and
ends (its first and last words, for narration).

## 2. Settle everything with the user, then set up the whole video
Ask the user anything you need before fanning out. A subagent can ask too, but its question
stops its section until they answer, and they only see the question, not the section's context.

Subagents working in parallel can't change anything video-wide, so decide it all before fanning
out: template, aspect ratio, color palette, captions, music, brand kit. Also decide the shared
look (design choices, media style, text density, pacing) that every section must follow.

## 3. Make one scene group per section
Each subagent owns whole scene groups, so every section needs its own:
- **Narration already in one scene group** (an uploaded recording, a talking head, or one long
  AI voiceover): split it at every section boundary in one \`${TOOL_NAMES.splitSceneGroup}\`
  call. K sections take K-1 split points. Note each section's scene_group_id from the split's
  result.
- **AI voiceover from a script**: add one voiceover scene group per section, in order, each
  with that section's script, using \`${TOOL_NAMES.addVoiceoverSceneGroup}\` with
  \`after_scene_group_id\` set to the previous section's group.
- **No narration (text on media)**: every scene is its own group. Add each section's first
  scene yourself, in order, with \`${TOOL_NAMES.addScene}\`. That scene is the section's anchor:
  its subagent owns it and adds the section's remaining scenes right after it, so the sections
  stay in order.

## 4. Fill the sections with subagents
Start one subagent per section, all in the same message (up to 10), each with
\`scene_group_ids\` set to its section's scene groups. A few run at once and the rest wait their
turn, so you don't need to split them across messages. Every subagent sees only its prompt, so
each prompt must carry:
- its section's purpose, its words or script, and what comes just before and after it;
- the shared look from step 2, word for word, the same in every prompt;
- for a section without narration, to add each new scene with \`after_scene_group_id\` set to
  its own last scene group, starting from its anchor, so its scenes stay inside its section;
- which skills to load before starting (the ones for this kind of video, e.g.
  \`${SKILL_NAMES.voiceoverVideoBasics}\` or \`${SKILL_NAMES.textVideoBasics}\`);
- what to report back: a short summary of what it built and anything it couldn't do.

Keep each report short. The per-section diffs already show what changed.

Each subagent lints and fixes its own section before it reports, so there's no need to ask for
that in its prompt. Subagents don't review how their scenes look: that's your job, once.

## 5. Check the whole video
Don't read the whole video state once the sections are filled: a long video's full state is large
enough to crowd out the rest of your context. Run \`${TOOL_NAMES.lintVideo}\` instead. The
sections come back already linted, so what's left should be the seams between them and anything
video-wide: fix those yourself. Then review the whole video with
\`${TOOL_NAMES.reviewSceneImages}\` as \`${SKILL_NAMES.reviewVideo}\` describes. When findings pile up
inside a section, don't fix them one by one: start a subagent for that section again, with the
same \`scene_group_ids\` and the findings to fix, several sections at a time.`;
    },
};
